import { FormikValues } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Card, FormHelperText, Grid } from '@material-ui/core';
import { Assignment as AssignmentIcon } from '@material-ui/icons';
import { InlineForm, InlineFormik } from '@sierralabs/react-material-ui-formik';

import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineSelect from '../../../common/inline/InlineSelect';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateHazard } from '../Hazard.hooks';
import {
  BaseHazardCardProps,
  Hazard,
  hazardSeverity,
  hazardProbability,
  HazardSeverity
} from '../Hazard.interface';
import { getRiskLabel } from '../Hazard.helpers';
import InlineHtml from '../../../common/inline/InlineHtml';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { FetchError } from '../../../common/api/api';
import InlineTextField from '../../../common/inline/InlineTextField';

const StyledCard = styled(Card)`
  .form-control {
    width: 150px;
  }
`;

const HazardResidualAssessmentCard: React.FC<BaseHazardCardProps> = props => {
  const { hazard, hazardVersion, onChange } = props;
  const [error, setError] = useState<FetchError | undefined>();
  const { updatedHazard, isUpdating, updateError, updateHazard } =
    useUpdateHazard(hazard.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(hazard.task.id || 0);
  const formikRef = useRef<FormikValues>();
  const onChangeRef = useRef(onChange);
  const { isFieldDisabled } = useTaskEntityFields(hazard.task);
  const disabled = !!hazardVersion;
  const data = hazardVersion || hazard;

  const handleOnChangeAttachment = (attachments: InlineFileValue[]) => {
    updateTask({ attachments });
    if (hazard.task) {
      hazard.task.attachments = attachments;
    }
    onChange?.(hazard);
  };

  useEffect(() => {
    if (updatedHazard) {
      onChangeRef.current?.(updatedHazard);
    }
  }, [updatedHazard]);

  useEffect(() => {
    if (updatedTask) {
      hazard.task = Object.assign(hazard.task || {}, updatedTask);
      onChange?.(hazard);
    }
  }, [updatedTask]);

  useEffect(() => {
    setError(updateTaskError || updateError);
  }, [updateTaskError, updateError]);

  return (
    <StyledCard>
      <CardTitle>
        <AssignmentIcon />
        Hazard Residual Assessment
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          error={error}
          isSubmitting={isUpdating}
          innerRef={(formik: any) => (formikRef.current = formik)}
          initialValues={{
            initialSeverity:
              data.initialSeverity || ('' as any as HazardSeverity),
            // residualSeverity: residualSeverity || '',
            residualProbability: data.residualProbability,
            residualRpn: data.residualRpn,
            residualRiskLabel: getRiskLabel(data.residualRpn),
            riskJustification: data.riskJustification || '',
            benefitRiskAnalysis: data.benefitRiskAnalysis || '',
            comments: data.comments || ''
          }}
          onSubmit={(dto: Partial<Hazard>) => updateHazard(dto)}
        >
          <InlineForm>
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12} lg={4}>
                <FormHelperText>Residual Severity</FormHelperText>
                <InlineSelect
                  name='initialSeverity'
                  label=''
                  // always disabled and uses initial severity
                  disabled
                  options={hazardSeverity.map(s => ({
                    value: s.value,
                    label: `${s.value} - ${s.label}`
                  }))}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <FormHelperText>Residual Probability</FormHelperText>
                <InlineSelect
                  name='residualProbability'
                  label=''
                  disabled
                  options={[
                    ...hazardProbability.map(s => ({
                      value: s.value,
                      label: `${s.value} - ${s.label}`
                    })),
                    { value: 0, label: '' }
                  ]}
                />
                <FormHelperText>
                  Calculated based on hazard causes
                </FormHelperText>
              </Grid>
              <Grid item xs={12} lg={2}>
                <FormHelperText>Residual RPN</FormHelperText>
                <InlineTextField disabled name='residualRpn' />
                <FormHelperText>
                  Calculated based on severity and probability
                </FormHelperText>
              </Grid>
              <Grid item xs={12} lg={2}>
                <FormHelperText>Residual Risk</FormHelperText>
                <InlineTextField disabled name='residualRiskLabel' />
              </Grid>
              <Grid item xs={12}>
                <InlineHtml
                  name='riskJustification'
                  label='Risk Justification'
                  disabled={
                    disabled ||
                    isUpdating ||
                    isFieldDisabled('riskJustification')
                  }
                  attachments={data.task.attachments || []}
                  onChangeAttachments={handleOnChangeAttachment}
                />
              </Grid>
              <Grid item xs={12}>
                <InlineHtml
                  name='benefitRiskAnalysis'
                  label='Benefit-Risk Analysis'
                  disabled={
                    disabled ||
                    isUpdating ||
                    isFieldDisabled('benefitRiskAnalysis')
                  }
                  attachments={data.task.attachments || []}
                  onChangeAttachments={handleOnChangeAttachment}
                />
              </Grid>
              <Grid item xs={12}>
                <InlineHtml
                  name='comments'
                  label='Comments'
                  disabled={
                    disabled || isUpdating || isFieldDisabled('comments')
                  }
                  attachments={data.task.attachments || []}
                  onChangeAttachments={handleOnChangeAttachment}
                />
              </Grid>
            </Grid>
          </InlineForm>
        </InlineFormik>
      </CardContent>
    </StyledCard>
  );
};

export default HazardResidualAssessmentCard;
